import { Controller } from "@hotwired/stimulus"
import { alignedMenuLeft, anchoredFixedPosition } from "../lib/anchored_menu_position"
import { effectiveZoom } from "../lib/select2_manager"
import { MOBILE_SHEET_BREAKPOINT } from "../lib/mobile_viewport"
import { showConfirmModal } from "../lib/custom_confirm"

// The behaviour of the HAML "More" / ⋯ menu (MoreMenuHelper#gl_more_menu), the
// twin of React MoreMenu (app/frontend/react/components/shared/MoreMenu.tsx).
// The helper writes MoreMenu's markup; this controller does what MoreMenu does
// and writes nothing MoreMenu would not:
// - opens on the trigger (not while it is held), focus on the first item;
// - arrows (wrapping) / Home / End over the items; Escape closes it, puts focus
//   back on the trigger and never closes a modal around it;
// - a picked item closes it and returns focus first, then its link runs or it
//   fires `more-menu:select` ({ value, item }) after any `confirm` text is
//   accepted (custom_confirm, never window.confirm); a held item does nothing;
// - a multi-check row (`menuitemcheckbox`) flips its check on a click, Space or
//   Enter and fires `more-menu:select` ({ value, item, checked, revert }) with
//   the menu still open and focus still on it (owner ruling 43); `revert()`
//   puts the check back when the caller's save fails;
// - a `button_to` row (a submit button) closes the menu and its form submits;
// - a click outside, Tab away, an outer scroll or a resize close it without
//   pulling focus back;
// - on a desktop it is `position: fixed` at its trigger (so no clipping parent
//   cuts it off); below 768px it opens in the shared bottom sheet with its own
//   Close, exactly as MoreMenu builds it;
// - it stays inside the popup its trigger sits in (the nearest `.modal-dialog`),
//   else the screen (owner ruling 47): left-aligned at the trigger when it
//   fits, else right-aligned to the trigger's right edge, else clamped;
// - while open, the list (or sheet) sits in a layer outside every transformed
//   ancestor — the trigger's `.modal`, else <body> — as MoreMenu portals it: an
//   open `.gl-modal` dialog is at `transform: scale(1)`, which would make the
//   dialog the box `position: fixed` measures from. The layer listens for the
//   keys, focus and pointer events the wrapper's actions handle, and stops
//   Escape before a Bootstrap modal's own listener. Tab from an item closes it
//   with focus back on the trigger, and the browser moves on from there.
const ITEM_SELECTOR = '[role="menuitem"], [role="menuitemcheckbox"], [role="menuitemradio"]'
const ANCHORED_CLASS = "gl-dropdown-menu--anchored"
const SHEET_CLASS = "gl-dropdown-menu--sheet"
const CHECK_MARK_CLASSES = ["ph", "ph-check", "gl-dropdown-item-end"] // MoreMenuItem's `checked` mark
const MENU_GAP_PX = 4 // DEFAULT_MENU_GAP_PX in React lib/menuPlacement.ts
// The fixed app chrome a menu must stay below (React useAnchoredMenu#usableViewport).
const FIXED_TOP_CHROME_SELECTORS = [".gl-navbar", ".gl-impersonation-banner"]

function isPhone() {
  return window.innerWidth < MOBILE_SHEET_BREAKPOINT
}

function viewportHeight() {
  return document.documentElement.clientHeight || window.innerHeight
}

function viewportWidth() {
  return document.documentElement.clientWidth || window.innerWidth
}

// The box the menu stays inside (React useAnchoredMenu#horizontalBounds): the
// trigger's open popup, else the viewport. Viewport px.
function horizontalBounds(trigger) {
  const width = viewportWidth()
  const dialog = trigger.closest(".modal-dialog")?.getBoundingClientRect()
  if (!dialog || dialog.width <= 0) return { left: 0, right: width }
  return { left: Math.max(0, dialog.left), right: Math.min(width, dialog.right) }
}

function chromeBottom() {
  return Math.max(0, ...FIXED_TOP_CHROME_SELECTORS.map((selector) => {
    const el = document.querySelector(selector)
    if (!el) return 0
    const style = window.getComputedStyle(el)
    if (style.position !== "fixed" || style.display === "none" || style.visibility === "hidden") return 0
    const rect = el.getBoundingClientRect()
    return rect.height > 0 ? rect.bottom : 0
  }))
}

export default class extends Controller {
  static targets = ["trigger", "menu", "caret"]

  connect() {
    this.isOpen = false
    this.pointerInside = false
    this.boundOutsideClick = this.#outsideClick.bind(this)
    this.boundOuterScroll = this.#outerScroll.bind(this)
    this.boundDismiss = this.dismiss.bind(this)
  }

  disconnect() {
    if (this.isOpen) this.dismiss()
  }

  toggle(event) {
    event.preventDefault()
    if (this.triggerTarget.getAttribute("aria-disabled") === "true") return
    this.isOpen ? this.dismiss() : this.open()
  }

  open() {
    if (this.isOpen) return
    this.isOpen = true
    this.phone = isPhone()
    // Held directly: once the list moves to its layer it is outside this.element,
    // where the Stimulus target lookup no longer finds it.
    this.menu = this.menuTarget
    this.triggerTarget.setAttribute("aria-expanded", "true")
    this.triggerTarget.setAttribute("aria-controls", this.menu.id)
    this.#setCaret("ph-caret-up")
    this.menu.classList.add("show")
    this.menu.addEventListener("click", this.boundPick ||= this.#pick.bind(this))
    this.menuHome = { parent: this.menu.parentNode, next: this.menu.nextSibling }
    this.#openLayer()

    if (this.phone) {
      this.#openSheet()
    } else {
      this.layer.append(this.menu)
      this.#position()
      window.addEventListener("scroll", this.boundOuterScroll, true)
      window.addEventListener("resize", this.boundDismiss)
    }
    document.addEventListener("click", this.boundOutsideClick)
    this.#items()[0]?.focus()
  }

  // Close and put focus back on the trigger (Escape, a picked item, the sheet's Close).
  close() {
    this.dismiss()
    this.triggerTarget.focus()
  }

  // Close without moving focus (click outside, Tab away, scroll, resize).
  dismiss() {
    if (!this.isOpen) return
    this.isOpen = false
    this.triggerTarget.setAttribute("aria-expanded", "false")
    this.triggerTarget.removeAttribute("aria-controls")
    this.#setCaret("ph-caret-down")
    const menu = this.menu
    menu.classList.remove("show")
    menu.removeEventListener("click", this.boundPick)
    if (this.sheet) this.#closeSheet()
    menu.removeAttribute("style")
    this.menuHome.parent.insertBefore(menu, this.menuHome.next)
    this.#closeLayer()
    window.removeEventListener("scroll", this.boundOuterScroll, true)
    window.removeEventListener("resize", this.boundDismiss)
    document.removeEventListener("click", this.boundOutsideClick)
  }

  keydown(event) {
    if (!this.isOpen) return
    if (event.key === "Escape") {
      event.preventDefault()
      event.stopPropagation() // a menu inside a modal: Escape closes the menu, never the modal too
      this.close()
      return
    }
    if (event.key === "Tab" && !this.phone) {
      this.close()
      return
    }
    const items = this.#items()
    if (items.length === 0) return
    const current = items.indexOf(document.activeElement)
    const target = {
      ArrowDown: () => items[current < 0 ? 0 : (current + 1) % items.length],
      ArrowUp: () => items[current < 0 ? items.length - 1 : (current - 1 + items.length) % items.length],
      Home: () => items[0],
      End: () => items[items.length - 1],
    }[event.key]
    if (!target) {
      // A menuitem link activates on Space too; a native link only on Enter.
      if (event.key === " " && event.target.tagName === "A" && event.target.matches(ITEM_SELECTOR)) {
        event.preventDefault()
        event.target.click()
      }
      return
    }
    event.preventDefault()
    target().focus()
  }

  // Tab away closes it; the sheet's own Close and overlay end a phone sheet.
  focusout(event) {
    if (!this.isOpen || this.phone || this.pointerInside) return
    if (!this.#inside(event.relatedTarget)) this.dismiss()
  }

  // A click on a non-focusable part of the menu blurs the item with no
  // relatedTarget; that is not leaving the menu.
  pointerdown() {
    this.pointerInside = true
    setTimeout(() => { this.pointerInside = false }, 0)
  }

  async #pick(event) {
    const item = event.target.closest(ITEM_SELECTOR)
    if (!item || !this.menu.contains(item)) return
    if (item.getAttribute("aria-disabled") === "true") {
      event.preventDefault()
      return
    }
    if (item.getAttribute("role") === "menuitemcheckbox") {
      this.#toggleCheck(item)
      return
    }
    this.close()
    if (item.tagName === "A") return // the link runs (Turbo, a download, a Bootstrap modal toggle)
    if (item.type === "submit") return // a button_to row: its form submits

    const confirmText = item.dataset.moreMenuConfirm
    if (confirmText && !(await showConfirmModal(confirmText))) return
    this.dispatch("select", { detail: { value: item.dataset.moreMenuValue, item } })
  }

  // Flip a multi-check row in place, drawn as MoreMenuItem draws `checked`:
  // aria-checked, and the check mark after the text (before any key cap).
  #toggleCheck(item) {
    const setChecked = (checked) => {
      item.setAttribute("aria-checked", String(checked))
      const mark = item.querySelector(`.${CHECK_MARK_CLASSES.join(".")}`)
      if (!checked) {
        mark?.remove()
      } else if (!mark) {
        const icon = document.createElement("i")
        icon.className = CHECK_MARK_CLASSES.join(" ")
        icon.setAttribute("aria-hidden", "true")
        item.querySelector(".gl-dropdown-item-text").after(icon)
      }
    }
    const checked = item.getAttribute("aria-checked") !== "true"
    setChecked(checked)
    this.dispatch("select", {
      detail: { value: item.dataset.moreMenuValue, item, checked, revert: () => setChecked(!checked) },
    })
  }

  #items() {
    return Array.from(this.menu.querySelectorAll(ITEM_SELECTOR))
  }

  #setCaret(name) {
    if (!this.hasCaretTarget) return
    this.caretTarget.classList.remove("ph-caret-up", "ph-caret-down")
    this.caretTarget.classList.add(name)
  }

  #outsideClick(event) {
    if (!this.#inside(event.target)) this.dismiss()
  }

  #inside(node) {
    return this.element.contains(node) || Boolean(this.layer?.contains(node))
  }

  #openLayer() {
    const layer = document.createElement("div")
    layer.setAttribute("data-more-menu-layer", "")
    this.layerListeners = {
      keydown: (event) => this.keydown(event),
      focusout: (event) => this.focusout(event),
      mousedown: () => this.pointerdown(),
    }
    Object.entries(this.layerListeners).forEach(([type, fn]) => layer.addEventListener(type, fn))
    ;(this.triggerTarget.closest(".modal") || document.body).append(layer)
    this.layer = layer
  }

  #closeLayer() {
    this.layer.remove()
    this.layer = null
  }

  #outerScroll(event) {
    if (event.target instanceof Node && this.menu.contains(event.target)) return
    this.dismiss()
  }

  // Fixed at the trigger, inside the usable viewport (below the fixed navbar)
  // and inside its popup, else the screen (horizontalBounds); viewport px
  // divided by the desktop zoom once.
  #position() {
    const menu = this.menu
    const zoom = effectiveZoom(menu) || 1
    const rect = this.triggerTarget.getBoundingClientRect()
    const top = chromeBottom()
    const bandHeight = Math.max(0, viewportHeight() - top)
    const anchor = { top: rect.top - top, bottom: rect.bottom - top }
    const placed = anchoredFixedPosition(anchor, menu.offsetHeight * zoom, bandHeight, MENU_GAP_PX)
    const width = menu.offsetWidth * zoom
    const left = alignedMenuLeft(rect, width, horizontalBounds(this.triggerTarget), MENU_GAP_PX)

    // Inline, because Bootstrap's `.navbar-nav .dropdown-menu` (static / absolute)
    // outranks `.gl-dropdown-menu--anchored` for a menu in the navbar list.
    menu.style.position = "fixed"
    menu.style.left = `${left / zoom}px`
    if (placed.top != null) menu.style.top = `${Math.max(top, placed.top + top) / zoom}px`
    if (placed.bottom != null) menu.style.bottom = `${Math.max(0, placed.bottom) / zoom}px`
    if (placed.maxHeight != null) {
      menu.style.maxHeight = `${Math.max(0, placed.maxHeight) / zoom}px`
      menu.style.overflowY = "auto"
    }
  }

  // MoreMenu's phone markup: overlay, then the sheet (handle, body with the list
  // and a Close), in the layer.
  #openSheet() {
    const menu = this.menu
    menu.classList.replace(ANCHORED_CLASS, SHEET_CLASS)

    const overlay = document.createElement("div")
    overlay.className = "gl-bottom-sheet-overlay open"
    overlay.setAttribute("aria-hidden", "true")
    overlay.addEventListener("click", () => this.close())

    const sheet = document.createElement("div")
    sheet.className = "gl-bottom-sheet open"
    const handle = document.createElement("div")
    handle.className = "gl-bottom-sheet-handle"
    handle.setAttribute("aria-hidden", "true")
    const body = document.createElement("div")
    body.className = "gl-bottom-sheet-body"
    const closeButton = document.createElement("button")
    closeButton.type = "button"
    closeButton.className = "gl-btn gl-btn-outline w-100 mt-2"
    closeButton.textContent = "Close"
    closeButton.addEventListener("click", () => this.close())
    body.append(menu, closeButton)
    sheet.append(handle, body)

    this.layer.append(overlay, sheet)
    this.sheet = { overlay, sheet }
  }

  #closeSheet() {
    this.menu.classList.replace(SHEET_CLASS, ANCHORED_CLASS)
    this.sheet.overlay.remove()
    this.sheet.sheet.remove()
    this.sheet = null
  }
}
